import { SMS_DELIVERY_UNAVAILABLE_MESSAGE_FA, type ErrorCode } from '../contracts/errors.js';

/**
 * One error type for the whole customer client.
 *
 * Every failure — a network drop, a timeout, a Core error body, a bridge that
 * is not deployed — arrives at the UI as a `CustomerApiError` with a stable
 * code and a Persian message that is safe to show. Components therefore never
 * inspect `Response` objects or `TypeError`s; they render `error.messageFa`.
 */

/** Codes the server can return, plus the client-only failures. */
export type CustomerErrorCode =
  | ErrorCode
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'ABORTED'
  | 'BRIDGE_UNAVAILABLE'
  | 'BRIDGE_INVALID_RESPONSE'
  | 'UNKNOWN_SERVICE'
  | 'SERVICE_DISABLED'
  | 'NOT_CONFIGURED';

/**
 * Persian copy for the failures a customer can actually reach.
 *
 * The server's own `message` is preferred whenever it is present, because the
 * OTP and request endpoints already answer in Persian with the specific reason
 * (cooldown seconds, attempt counts). These are the fallbacks for transport
 * failures, where there is no server message at all.
 */
const FALLBACK_MESSAGES_FA: Partial<Record<CustomerErrorCode, string>> = {
  NETWORK_ERROR: 'ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی کنید.',
  TIMEOUT: 'پاسخ سرور بیش از حد طول کشید. لطفاً دوباره تلاش کنید.',
  ABORTED: 'درخواست لغو شد.',
  BRIDGE_UNAVAILABLE: 'ورود از این سایت در حال حاضر امکان‌پذیر نیست. لطفاً بعداً تلاش کنید.',
  BRIDGE_INVALID_RESPONSE: 'پاسخ سرویس ورود نامعتبر بود. لطفاً دوباره تلاش کنید.',
  UNKNOWN_SERVICE: 'این بخش شناخته نشد.',
  SERVICE_DISABLED: 'این بخش در حال حاضر فعال نیست.',
  NOT_CONFIGURED: 'این بخش هنوز تنظیم نشده است.',
  UNAUTHENTICATED: 'برای ادامه باید وارد حساب کاربری خود شوید.',
  TOKEN_EXPIRED: 'نشست شما منقضی شده است. لطفاً دوباره وارد شوید.',
  TOKEN_INVALID: 'نشست شما معتبر نیست. لطفاً دوباره وارد شوید.',
  FORBIDDEN: 'اجازه انجام این کار را ندارید.',
  NOT_FOUND: 'موردی که خواستید پیدا نشد.',
  RATE_LIMITED: 'تعداد درخواست‌ها زیاد بود. کمی بعد دوباره تلاش کنید.',
  VALIDATION_ERROR: 'اطلاعات وارد‌شده کامل یا درست نیست.',
  OTP_INVALID: 'کد تأیید نادرست است.',
  OTP_EXPIRED: 'کد تأیید منقضی شده است. کد جدید بگیرید.',
  OTP_ATTEMPTS_EXCEEDED: 'تعداد تلاش‌های نادرست زیاد بود. کد جدید بگیرید.',
  OTP_COOLDOWN: 'کمی بعد برای دریافت کد جدید تلاش کنید.',
  SMS_DELIVERY_UNAVAILABLE: SMS_DELIVERY_UNAVAILABLE_MESSAGE_FA,
  INTERNAL_ERROR: 'خطای غیرمنتظره‌ای رخ داد. لطفاً دوباره تلاش کنید.',
  UPSTREAM_ERROR: 'سرویس مقصد پاسخ نداد. لطفاً دوباره تلاش کنید.',
  CONFLICT: 'این درخواست با وضعیت فعلی سرویس سازگار نیست.',
  REQUEST_INVALID_STATE: 'این درخواست در وضعیتی نیست که بتوان این کار را انجام داد.',
  IDEMPOTENCY_CONFLICT: 'این درخواست قبلاً با اطلاعات دیگری ثبت شده است.',
};

/** Which codes are worth offering a "try again" button for. */
const RETRYABLE: ReadonlySet<string> = new Set<string>([
  'NETWORK_ERROR',
  'TIMEOUT',
  'RATE_LIMITED',
  'INTERNAL_ERROR',
  'UPSTREAM_ERROR',
  'BRIDGE_UNAVAILABLE',
  'BRIDGE_INVALID_RESPONSE',
  'PAYMENT_PROVIDER_UNAVAILABLE',
]);

export interface FieldIssue {
  field: string;
  messageFa: string;
}

export class CustomerApiError extends Error {
  readonly code: CustomerErrorCode;
  readonly status: number;
  readonly details?: unknown;
  /** True when the same action may succeed if attempted again. */
  readonly retryable: boolean;
  /** Per-field messages extracted from a validation error, when present. */
  readonly fieldIssues: readonly FieldIssue[];
  /** Correlation id from the server, for support. */
  readonly requestId?: string;

  constructor(
    code: CustomerErrorCode,
    messageFa?: string,
    options: {
      status?: number;
      details?: unknown;
      retryable?: boolean;
      fieldIssues?: readonly FieldIssue[];
      requestId?: string;
    } = {},
  ) {
    super(messageFa ?? FALLBACK_MESSAGES_FA[code] ?? 'خطای نامشخص رخ داد.');
    this.name = 'CustomerApiError';
    this.code = code;
    this.status = options.status ?? 0;
    this.details = options.details;
    this.retryable = options.retryable ?? RETRYABLE.has(code);
    this.fieldIssues = options.fieldIssues ?? [];
    this.requestId = options.requestId;
  }

  /** The message to display; always Persian and always safe. */
  get messageFa(): string {
    return this.message;
  }

  /** True when the caller must authenticate before retrying. */
  get requiresAuth(): boolean {
    return (
      this.code === 'UNAUTHENTICATED' ||
      this.code === 'TOKEN_EXPIRED' ||
      this.code === 'TOKEN_INVALID'
    );
  }

  withRequestId(requestId: string | undefined): CustomerApiError {
    if (!requestId || this.requestId) return this;
    return new CustomerApiError(this.code, this.message, {
      status: this.status,
      details: this.details,
      retryable: this.retryable,
      fieldIssues: this.fieldIssues,
      requestId,
    });
  }

  static unknown(messageFa?: string): CustomerApiError {
    return new CustomerApiError('INTERNAL_ERROR', messageFa);
  }

  static network(): CustomerApiError {
    return new CustomerApiError('NETWORK_ERROR');
  }

  static timeout(): CustomerApiError {
    return new CustomerApiError('TIMEOUT');
  }

  static aborted(): CustomerApiError {
    return new CustomerApiError('ABORTED');
  }
}

interface ErrorBodyLike {
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
    request_id?: string;
  };
}

/**
 * Extract per-field issues from a Core validation error.
 *
 * Core reports zod failures as `details.fields` — either an array of
 * `{ path, message }` or a map. Both shapes are normalised here so the form can
 * annotate inputs regardless of which route produced the error.
 */
export function extractFieldIssues(details: unknown): FieldIssue[] {
  if (!details || typeof details !== 'object') return [];
  const fields = (details as { fields?: unknown }).fields;
  if (!fields) return [];

  const issues: FieldIssue[] = [];
  if (Array.isArray(fields)) {
    for (const entry of fields) {
      if (!entry || typeof entry !== 'object') continue;
      const record = entry as { path?: unknown; field?: unknown; message?: unknown };
      const rawPath = record.path ?? record.field;
      const field = Array.isArray(rawPath) ? rawPath.join('.') : String(rawPath ?? '');
      if (!field) continue;
      issues.push({
        field,
        messageFa: typeof record.message === 'string' && record.message ? record.message : 'مقدار نامعتبر است',
      });
    }
    return issues;
  }

  if (typeof fields === 'object') {
    for (const [field, value] of Object.entries(fields as Record<string, unknown>)) {
      const message =
        typeof value === 'string'
          ? value
          : Array.isArray(value) && typeof value[0] === 'string'
            ? (value[0] as string)
            : 'مقدار نامعتبر است';
      issues.push({ field, messageFa: message });
    }
  }
  return issues;
}

/**
 * Codes whose customer message is fixed by the contract.
 *
 * For these the server's `message` is ignored: whatever a relying site's bridge
 * or an older Core puts in the body, the customer reads the agreed sentence and
 * never a provider's wording.
 */
const FIXED_MESSAGES_FA: Partial<Record<CustomerErrorCode, string>> = {
  SMS_DELIVERY_UNAVAILABLE: SMS_DELIVERY_UNAVAILABLE_MESSAGE_FA,
};

/** Convert a Core error body into the client error type. */
export function errorFromBody(body: unknown, status: number): CustomerApiError {
  const envelope = (body ?? {}) as ErrorBodyLike;
  const rawCode = envelope.error?.code;
  const code = (rawCode ?? statusToCode(status)) as CustomerErrorCode;
  const details = envelope.error?.details;
  const fieldIssues = extractFieldIssues(details);
  const message = FIXED_MESSAGES_FA[code] ?? envelope.error?.message;
  return new CustomerApiError(code, message || undefined, {
    status,
    details,
    fieldIssues,
    ...(envelope.error?.request_id ? { requestId: envelope.error.request_id } : {}),
  });
}

function statusToCode(status: number): CustomerErrorCode {
  if (status === 401) return 'UNAUTHENTICATED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 429) return 'RATE_LIMITED';
  if (status >= 500) return 'INTERNAL_ERROR';
  return 'VALIDATION_ERROR';
}

/** Normalise anything thrown by a transport into a `CustomerApiError`. */
export function toCustomerApiError(error: unknown): CustomerApiError {
  if (error instanceof CustomerApiError) return error;
  if (error instanceof DOMException && error.name === 'AbortError') {
    return CustomerApiError.aborted();
  }
  if (error instanceof Error) {
    if (error.name === 'AbortError') return CustomerApiError.timeout();
    return new CustomerApiError('NETWORK_ERROR', error.message || undefined);
  }
  return CustomerApiError.unknown();
}
